import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { WHY, CONTACT } from "@/data/site";
import { Eyebrow, MaskLines, Reveal } from "./Reveal";
import { EASE } from "@/lib/scroll";

const OwnerCard = () => (
  <div data-testid="why-owner-card" className="mt-10 rounded-[24px] bg-white border border-[#111511]/[0.08] p-6 shadow-[0_30px_60px_-45px_rgba(17,21,17,0.35)]">
    <div className="flex items-center gap-4">
      <span className="w-14 h-14 rounded-2xl bg-[#111511] text-white font-head text-lg font-semibold flex items-center justify-center">KA</span>
      <div>
        <p className="font-head text-lg font-semibold text-[#111511]">{CONTACT.owner}</p>
        <p className="text-sm text-[#5B615A]">Inhaber · Ihr direkter Ansprechpartner</p>
      </div>
    </div>
    <div className="mt-5 grid grid-cols-2 gap-2">
      <a href={CONTACT.phoneHref} target="_top" data-testid="why-call-button" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#111511] text-white py-3 text-[15px] font-semibold transition-colors duration-300 hover:bg-[#2F5D3A]"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
      <a href={CONTACT.emailHref} target="_top" data-testid="why-email-button" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full border border-[#111511]/15 py-3 px-3 text-sm font-medium text-[#111511] transition-colors duration-300 hover:border-[#111511] [overflow-wrap:anywhere]"><Mail className="w-4 h-4 shrink-0" /> {CONTACT.email}</a>
      <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="why-whatsapp-button" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full border border-[#111511]/15 py-3 text-sm font-medium text-[#111511] transition-colors duration-300 hover:border-[#111511]"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
    </div>
  </div>
);

const WhyRow = ({ w, i }) => (
  <motion.li data-testid={`why-item-${i}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
    className="group relative grid grid-cols-[auto_1fr] sm:grid-cols-[64px_auto_1fr] gap-x-5 gap-y-2 items-start py-7 sm:py-8 border-t border-[#111511]/10 last:border-b">
    <span className="absolute left-0 top-0 h-[2px] w-full origin-left scale-x-0 bg-[#5E8C3A] transition-transform duration-700 group-hover:scale-x-100" />
    <span className="hidden sm:block font-head text-3xl font-semibold tracking-tight text-[#111511]/15 transition-colors duration-500 group-hover:text-[#5E8C3A] tabular-nums">0{i + 1}</span>
    <span className="w-12 h-12 rounded-2xl bg-[#EEF3E6] text-[#2F5D3A] flex items-center justify-center transition-colors duration-500 group-hover:bg-[#111511] group-hover:text-[#9CC26A]"><w.icon className="w-5 h-5" strokeWidth={1.6} /></span>
    <div>
      <h3 className="font-head text-xl sm:text-2xl font-semibold tracking-tight text-[#111511]">{w.title}</h3>
      <p className="mt-2 text-[15px] sm:text-base leading-relaxed text-[#5B615A] max-w-xl">{w.text}</p>
    </div>
  </motion.li>
);

export const Why = () => (
  <section id="warum" data-testid="why-section" className="py-24 lg:py-36">
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div id="ueber-uns" className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <Eyebrow>Warum K&amp;A Gartenservice?</Eyebrow>
          <h2 className="mt-6 font-head font-semibold text-[2.4rem] sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] text-[#111511]">
            <MaskLines inView lines={["Sechs Gründe,", <span className="text-[#5E8C3A]">uns den Garten</span>, "anzuvertrauen."]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5B615A]">
              K&amp;A Gartenservice ist ein junger, mobiler Gartenservice aus Bottrop. Wir kommen zu Ihnen, kümmern uns um die regelmäßige Pflege – und Sie genießen einfach Ihren Garten.
            </p>
            <OwnerCard />
          </Reveal>
        </div>
      </div>
      <ol className="lg:col-span-7">
        {WHY.map((w, i) => <WhyRow key={w.title} w={w} i={i} />)}
      </ol>
    </div>
  </section>
);
